import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { GENERATED_SHIFT_RATIOS, buildGeneratePayload, validateGenerateForm } from '@/lib/recurrence';
import { scheduleRowState } from '@/lib/scheduleHome';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** RP-3: the ONE participant-schedule table (PSI rows + Generate popover), shared by the Schedule home and Funding ▸ Recurring Services. */
export function ParticipantScheduleRows({ items = [], generating, genForm, setGenForm, genOpenFor, setGenOpenFor, onGenerate, canGenerate = true, showRate = true }) {
  const cols = showRate ? 7 : 6;
  return (
    <Table data-testid="participant-schedule-rows">
      <TableHeader><TableRow><TableHead>Service</TableHead><TableHead>Agreement</TableHead><TableHead>Day</TableHead><TableHead>Time</TableHead>{showRate && <TableHead>Rate (read-only)</TableHead>}<TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
      <TableBody>
        {items.length === 0 ? <TableRow><TableCell colSpan={cols} className="text-center py-8 text-muted-foreground" data-testid="schedule-rows-empty">No schedules yet</TableCell></TableRow>
        : items.map((si) => {
          const st = scheduleRowState(si);
          return (
            <TableRow key={si.id} data-testid={`schedule-row-${si.id}`}>
              <TableCell className="font-medium">{si.service_name || 'N/A'}</TableCell>
              <TableCell className="text-xs">
                {st.linked ? <span data-testid={`schedule-row-agreement-${si.id}`}>{si.agreement_no || 'Linked'}{si.support_item_number ? ` · ${si.support_item_number}` : ''}</span>
                  : <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px]" title={st.reason} data-testid={`schedule-row-unlinked-${si.id}`}>{st.badge || 'Not linked'}</Badge>}
              </TableCell>
              <TableCell>{si.recurrence_label || DAY_NAMES[si.day_of_week] || 'N/A'}</TableCell>
              <TableCell className="font-mono text-xs tabular-nums">{si.start_time} - {si.end_time}</TableCell>
              {showRate && (
                <TableCell data-testid={`si-rate-${si.id}`}>
                  {(() => {
                    const vers = si.pricing?.versions || [];
                    const open = vers.find((v) => !v.effective_to) || vers[vers.length - 1];
                    const rate = si.pricing?.current_rate ?? open?.rate;
                    if (rate == null) return <span className="text-[10px] text-muted-foreground">—</span>;
                    return (
                      <div className="space-y-0.5">
                        <span className="font-mono text-xs tabular-nums">${Number(rate).toFixed(2)}/hr</span>
                        {open?.effective_from && <p className="text-[10px] text-muted-foreground">from {open.effective_from}</p>}
                        {si.support_ratio && <p className="text-[10px] text-muted-foreground">typical ratio {si.support_ratio} (informational)</p>}
                      </div>
                    );
                  })()}
                </TableCell>
              )}
              <TableCell>
                {si.archived_at ? <Badge className="bg-slate-100 text-slate-600 border-slate-300 text-[9px]">Archived</Badge>
                : si.last_generated_at ? (
                  <div className="space-y-0.5">
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[9px]" data-testid={`generated-badge-${si.id}`}><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Generated</Badge>
                    <p className="text-[10px] text-muted-foreground">{si.last_generated_period_start} — {si.last_generated_period_end}</p>
                    <p className="text-[10px] text-muted-foreground">{si.last_generated_count > 0 ? `${si.last_generated_count} shift(s) created last run` : 'Up to date — no new shifts last run'}</p>
                  </div>
                ) : <span className="text-[10px] text-muted-foreground">Not generated</span>}
              </TableCell>
              <TableCell>
                <Popover open={genOpenFor === si.id} onOpenChange={(o) => setGenOpenFor(o ? si.id : null)}>
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="outline" disabled={generating === si.id || st.disabled || !canGenerate} title={st.reason || (!canGenerate ? 'Requires the roster.manage permission' : 'Generate shifts from this schedule')} data-testid={`generate-shifts-${si.id}`}>
                      <Zap className="h-3 w-3 mr-1" /> {generating === si.id ? 'Generating...' : 'Generate'}{(st.disabled || !canGenerate) && <span className="ml-1 text-[10px] text-muted-foreground">· {st.reason ? (st.linked ? 'archived' : 'not linked') : 'roster.manage'}</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 space-y-3" data-testid={`generate-popover-${si.id}`}>
                    <div className="space-y-2"><Label className="text-xs">Shift ratio</Label>
                      <Select value={genForm.support_ratio || '1:1'} onValueChange={(v) => setGenForm({ ...genForm, support_ratio: v })}>
                        <SelectTrigger data-testid="gen-ratio"><SelectValue /></SelectTrigger>
                        <SelectContent>{GENERATED_SHIFT_RATIOS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label className="text-xs">Method</Label>
                      <Select value={genForm.mode} onValueChange={(v) => setGenForm({ ...genForm, mode: v })}>
                        <SelectTrigger data-testid="gen-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PRESET">Preset</SelectItem>
                          <SelectItem value="RANGE">Date range</SelectItem>
                          <SelectItem value="COUNT">Occurrence count</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {genForm.mode === 'PRESET' && (
                      <div className="space-y-2"><Label className="text-xs">Preset</Label>
                        <Select value={genForm.preset} onValueChange={(v) => setGenForm({ ...genForm, preset: v })}>
                          <SelectTrigger data-testid="gen-preset"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NEXT_4_WEEKS">Next 4 weeks</SelectItem>
                            <SelectItem value="THIS_MONTH">This month</SelectItem>
                            <SelectItem value="THIS_QUARTER">This quarter</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {genForm.mode === 'RANGE' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" data-testid="gen-from" value={genForm.from_date} onChange={(e) => setGenForm({ ...genForm, from_date: e.target.value })} /></div>
                        <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" data-testid="gen-to" value={genForm.to_date} onChange={(e) => setGenForm({ ...genForm, to_date: e.target.value })} /></div>
                      </div>
                    )}
                    {genForm.mode === 'COUNT' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1"><Label className="text-xs">Count</Label><Input type="number" min="1" max="100" data-testid="gen-count" value={genForm.count} onChange={(e) => setGenForm({ ...genForm, count: e.target.value })} /></div>
                        <div className="space-y-1"><Label className="text-xs">From (opt.)</Label><Input type="date" data-testid="gen-count-from" value={genForm.from_date} onChange={(e) => setGenForm({ ...genForm, from_date: e.target.value })} /></div>
                      </div>
                    )}
                    <Button size="sm" className="w-full" data-testid={`gen-submit-${si.id}`}
                      onClick={() => { const err = validateGenerateForm(genForm); if (err) { toast.error(err); return; } onGenerate(si.id, buildGeneratePayload(genForm)); }}>
                      Generate shifts
                    </Button>
                  </PopoverContent>
                </Popover>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export default ParticipantScheduleRows;
